import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Corners from "./Corners";

const STEPS = [
  {
    n: "01",
    name: "META AD",
    body: "Compliance-friendly creatives target your niche in your licensed states.",
  },
  {
    n: "02",
    name: "LANDING PAGE",
    body: "Hyper-niche opt-in built for one job: register for the webinar.",
  },
  {
    n: "03",
    name: "WEBINAR",
    body: "Pre-educate, pre-qualify, and close 50 people at once — not one-by-one.",
  },
  {
    n: "04",
    name: "APPLICATION",
    body: "Warm, informed leads self-select and book a pre-approval application.",
  },
  {
    n: "05",
    name: "CLOSED DEAL",
    body: "A pipeline you own — plus a retargeting list that compounds every launch.",
  },
];

const PILLARS = [
  {
    label: "OPPORTUNITY CREATION",
    body: "Direct-to-consumer Meta ads → hyper-niche landing page → webinar registration. You own every lead.",
  },
  {
    label: "OPPORTUNITY CONVERSION",
    body: "1-to-many webinar replaces 50 individual consults. Pre-educate, pre-qualify, close the serious ones.",
  },
  {
    label: "OPERATIONS",
    body: "Full GHL CRM automation. Speed-to-lead under 2 minutes. 90-day nurture built in. Zero manual follow-up.",
  },
];

export default function System() {
  const flowRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: flowRef,
    offset: ["start 80%", "end 40%"],
  });
  const pathLen = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      id="the-system"
      className="relative py-32 md:py-48"
      data-testid="system-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8 flex items-center gap-4"
        >
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#F0ECE4]/40">
            [003] —
          </span>
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]">
            HOW WAM WORKS
          </span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-display max-w-[1000px]"
          style={{ fontSize: "clamp(36px, 5.4vw, 84px)", lineHeight: 0.95, letterSpacing: "-0.035em" }}
        >
          A system that generates leads
          <br />
          <span className="text-[#C9A84C]">while you sleep.</span>
        </motion.h2>

        {/* Funnel flow */}
        <div ref={flowRef} className="relative mt-24">
          {/* horizontal connecting line for md+ */}
          <div className="hidden md:block absolute left-[8%] right-[8%] top-[42px] h-px overflow-hidden">
            <div className="absolute inset-0 bg-[#F0ECE4]/8" />
            <motion.div
              style={{ scaleX: pathLen, transformOrigin: "left" }}
              className="absolute inset-0 bg-[#C9A84C]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
                data-testid={`funnel-step-${i}`}
              >
                <div className="mb-6 flex md:justify-center">
                  <div className="relative flex h-[84px] w-[84px] items-center justify-center border wam-hairline-strong">
                    <span className="font-mono text-[11px] tracking-[0.2em] text-[#C9A84C]">
                      {s.n}
                    </span>
                    <span className="absolute -top-[6px] -left-[6px] h-[12px] w-[12px] border-t border-l border-[#C9A84C]" />
                    <span className="absolute -bottom-[6px] -right-[6px] h-[12px] w-[12px] border-b border-r border-[#C9A84C]" />
                  </div>
                </div>
                <div className="font-display text-[15px] tracking-[-0.02em]">
                  {s.name}
                </div>
                <div className="mt-2 font-body text-[13px] leading-[1.6] text-[#F0ECE4]/55 md:text-center max-w-[260px] md:mx-auto">
                  {s.body}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Pillars */}
        <div className="mt-32 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ y: -6 }}
              className="wam-card group"
              data-testid={`pillar-${i}`}
            >
              <Corners />
              <div className="h-[2px] w-10 bg-[#C9A84C]" />
              <div className="mt-6 font-mono text-[11px] tracking-[0.28em] text-[#F0ECE4]/60">
                PILLAR {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-3 font-display text-[22px] tracking-[-0.02em] leading-[1.1]">
                {p.label}
              </div>
              <p className="mt-4 font-body text-[14px] leading-[1.7] text-[#F0ECE4]/60">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
